/**
 * v3.21-1600 · Partner Content Manager (INTERNAL ADMIN ONLY).
 *
 * One screen to Upload / Edit / Publish / Archive / Restore / Replace
 * Version / Change Visibility / View Downloads / View Acknowledgements.
 *
 * All writes go to /api/content-manager/* — the router requires admin.
 * Partners never see this page.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  FileText, Search as SearchIcon, Plus, Upload, Loader2, Archive, RotateCcw,
  History, Download as DownloadIcon, Eye, ShieldCheck, Star, AlertTriangle,
  X, Save, Building2, Bell, BarChart3, Folder, LayoutDashboard, Layers, Trash2,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { AdminDashboardTab, RequiredComplianceTab, AnalyticsTab, FoldersTab, BulkCreateModal, InlineViewer } from './PartnerContentManager2Parts';

const humanize = (t) => (t || '').replaceAll('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const VISIBILITY_LABEL = {
  all_partners:       'All Partners',
  internal_only:      'Internal Only',
  selected_companies: 'Selected Companies',
  selected_roles:     'Selected Roles',
};

export default function PartnerContentManagerPage() {
  const [enums, setEnums]     = useState({ resource_types: [], visibility_kinds: [], allowed_ext: [], blocked_ext: [], max_upload_bytes: 100 * 1024 * 1024 });
  const [rows, setRows]       = useState([]);
  const [type, setType]       = useState('');
  const [q, setQ]             = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);       // resource being edited/created
  const [versionsOf, setVersionsOf] = useState(null); // resource whose versions are open
  const [downloadsOf, setDownloadsOf] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [viewerOf, setViewerOf] = useState(null);
  const [activeTab, setActiveTab] = useState('content');
  const [highlightId, setHighlightId] = useState(null);   // v3.21-1604 · flash newly-published row

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/content-manager/resources', {
        params: { type: type || undefined, q: q || undefined, include_archived: showArchived },
      });
      setRows(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  }, [type, q, showArchived]);

  useEffect(() => {
    (async () => {
      try { const r = await api.get('/content-manager/enums'); setEnums((s) => ({ ...s, ...r.data })); }
      catch { /* noop */ }
    })();
    load();
  }, [load]);

  const featured = useMemo(() => rows.filter((r) => r.is_featured && !r.archived).length, [rows]);
  const required = useMemo(() => rows.filter((r) => r.is_required && !r.archived).length, [rows]);

  return (
    <div className="p-6 space-y-4" data-testid="partner-content-manager-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="pcm-title">
            <FileText className="h-6 w-6 text-emerald-700" /> Partner Content Manager
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Internal-only. Publish once — the Partner Resource Library reflects changes immediately.
          </p>
        </div>
        <Button onClick={() => setBulkOpen(true)} variant="outline" data-testid="pcm-bulk"><Layers className="h-4 w-4 mr-1" />Bulk Create</Button>
        <Button onClick={() => setEditing({ isNew: true, form: { type: 'finance_documents', title: '', description: '', tags: [], visibility_kind: 'all_partners', visibility_company_ids: [], visibility_roles: [], is_featured: false, is_required: false, announcement_dismissable: true } })} className="bg-emerald-700 hover:bg-emerald-800" data-testid="pcm-new">
          <Plus className="h-4 w-4 mr-1" /> New Resource
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 max-w-3xl">
          <TabsTrigger value="dashboard" data-testid="pcm-tab-dashboard"><LayoutDashboard className="h-3.5 w-3.5 mr-1" />Dashboard</TabsTrigger>
          <TabsTrigger value="content"   data-testid="pcm-tab-content"><FileText className="h-3.5 w-3.5 mr-1" />Content</TabsTrigger>
          <TabsTrigger value="required"  data-testid="pcm-tab-required"><ShieldCheck className="h-3.5 w-3.5 mr-1" />Required</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="pcm-tab-analytics"><BarChart3 className="h-3.5 w-3.5 mr-1" />Analytics</TabsTrigger>
          <TabsTrigger value="folders"   data-testid="pcm-tab-folders"><Folder className="h-3.5 w-3.5 mr-1" />Folders</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard" className="mt-4"><AdminDashboardTab /></TabsContent>
        <TabsContent value="required"  className="mt-4"><RequiredComplianceTab /></TabsContent>
        <TabsContent value="analytics" className="mt-4"><AnalyticsTab /></TabsContent>
        <TabsContent value="folders"   className="mt-4"><FoldersTab /></TabsContent>
        <TabsContent value="content"   className="mt-4">

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total"      value={rows.length}    icon={FileText} testId="pcm-stat-total" />
        <StatCard label="Featured"   value={featured}       icon={Star}     testId="pcm-stat-featured" />
        <StatCard label="Required"   value={required}       icon={ShieldCheck} testId="pcm-stat-required" />
        <StatCard label="Archived"   value={rows.filter((r) => r.archived).length} icon={Archive} testId="pcm-stat-archived" />
      </div>

      <Card>
        <CardContent className="py-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input placeholder="Search title, description, tags, filename" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} className="pl-8 h-9" data-testid="pcm-search" />
          </div>
          <select value={type} onChange={(e) => setType(e.target.value)} className="h-9 border rounded-md text-sm px-2" data-testid="pcm-type">
            <option value="">All types</option>
            {enums.resource_types.map((t) => <option key={t} value={t}>{humanize(t)}</option>)}
          </select>
          <label className="flex items-center gap-1 text-sm text-slate-600">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="pcm-show-archived" />
            Show archived
          </label>
          <Button size="sm" onClick={load} disabled={loading} data-testid="pcm-apply">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null} Apply
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm uppercase tracking-wider text-slate-500">Content ({rows.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="pcm-table">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Title</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Visibility</th>
                <th className="text-center px-3 py-2">Flags</th>
                <th className="text-left px-3 py-2">Current Version</th>
                <th className="text-left px-3 py-2">Last Updated</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400 italic">No resources match.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className={`border-t hover:bg-slate-50 align-top transition-colors ${highlightId === r.id ? 'bg-emerald-50 ring-1 ring-emerald-300' : ''}`} data-testid={`pcm-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-800">{r.title}</div>
                    <div className="text-[11px] text-slate-500 line-clamp-1">{r.description}</div>
                    {(r.tags || []).slice(0, 4).map((t) => <Badge key={t} variant="outline" className="mr-1 mt-1 text-[10px] py-0 h-4">{t}</Badge>)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{humanize(r.type)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <Badge variant="outline" className="text-[10px] py-0 h-4">{VISIBILITY_LABEL[r.visibility_kind] || r.visibility_kind}</Badge>
                  </td>
                  <td className="px-3 py-2 text-center whitespace-nowrap">
                    {r.is_featured && <Star className="inline h-3.5 w-3.5 text-amber-500 mr-1" title="Featured" />}
                    {r.is_required && <ShieldCheck className="inline h-3.5 w-3.5 text-rose-600 mr-1" title="Required" />}
                    {r.archived    && <Archive className="inline h-3.5 w-3.5 text-slate-400" title="Archived" />}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs">
                    {r.current_version_id ? (r.versions || []).find((v) => v.id === r.current_version_id)?.version : <span className="text-slate-400 italic">no file</span>}
                    <div className="text-[10px] text-slate-500">{(r.versions || []).length} version(s)</div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{new Date(r.last_updated).toLocaleString()}</td>
                  <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => setViewerOf(r)} data-testid={`pcm-view-${r.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="outline" onClick={() => setEditing({ isNew: false, form: { ...r } })} data-testid={`pcm-edit-${r.id}`}>Edit</Button>
                    <Button size="sm" variant="outline" onClick={() => setVersionsOf(r)} data-testid={`pcm-versions-${r.id}`}><History className="h-3.5 w-3.5 mr-1" />Versions</Button>
                    <Button size="sm" variant="outline" onClick={() => setDownloadsOf(r)} data-testid={`pcm-downloads-${r.id}`}><DownloadIcon className="h-3.5 w-3.5 mr-1" />Log</Button>
                    {!r.archived
                      ? <Button size="sm" variant="outline" onClick={async () => { await api.post(`/content-manager/resources/${r.id}/archive`); toast.success('Archived'); load(); }} data-testid={`pcm-archive-${r.id}`}><Archive className="h-3.5 w-3.5" /></Button>
                      : <Button size="sm" variant="outline" onClick={async () => { await api.post(`/content-manager/resources/${r.id}/restore`); toast.success('Restored'); load(); }} data-testid={`pcm-restore-${r.id}`}><RotateCcw className="h-3.5 w-3.5" /></Button>}
                    <Button size="sm" variant="outline" className="text-rose-600 hover:bg-rose-50" onClick={async () => {
                      if (!window.confirm(`Delete "${r.title}"?  Partners will lose access immediately.  This is a soft-delete (audit trail preserved).`)) return;
                      try { await api.delete(`/content-manager/resources/${r.id}`); toast.success('Deleted'); load(); }
                      catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
                    }} data-testid={`pcm-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>

      {editing && (
        <ResourceEditor
          resource={editing.form}
          isNew={editing.isNew}
          enums={enums}
          onClose={() => setEditing(null)}
          onSaved={(payload) => {
            const id = (payload && typeof payload === 'object') ? payload.id : null;
            const viewNow = !!(payload && payload.viewNow);
            setEditing(null);
            load().then(() => {
              if (id) {
                setHighlightId(id);
                // ensure it scrolls into view once the table repaints
                requestAnimationFrame(() => {
                  const el = document.querySelector(`[data-testid="pcm-row-${id}"]`);
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                });
                setTimeout(() => setHighlightId(null), 5000);
              }
              if (viewNow && id) {
                // small delay so the table has painted
                setTimeout(() => {
                  const r = { id, title: (editing?.form?.title || ''), current_version: { original_filename: '' } };
                  setViewerOf(r);
                }, 100);
              }
            });
          }}
        />
      )}
      {bulkOpen && (
        <BulkCreateModal enums={enums} onClose={() => setBulkOpen(false)} onCreated={() => { setBulkOpen(false); load(); }} />
      )}
      {viewerOf && (
        <InlineViewer resource={viewerOf} onClose={() => setViewerOf(null)} />
      )}
      {versionsOf && (
        <VersionsModal resource={versionsOf} onClose={() => { setVersionsOf(null); load(); }} />
      )}
      {downloadsOf && (
        <DownloadsModal resource={downloadsOf} onClose={() => setDownloadsOf(null)} />
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, testId }) {
  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-2"><CardTitle className="text-xs flex items-center gap-2 text-slate-500"><Icon className="h-3.5 w-3.5 text-emerald-700" />{label}</CardTitle></CardHeader>
      <CardContent><div className="text-2xl font-bold">{value}</div></CardContent>
    </Card>
  );
}

function ResourceEditor({ resource, isNew, enums, onClose, onSaved }) {
  const [form, setForm] = useState({ ...resource });
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [uploadPct, setUploadPct] = useState(0);
  const fileRef = useRef(null);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const set = form;

  const MAX_BYTES = enums.max_upload_bytes || 100 * 1024 * 1024;
  const ALLOWED   = (enums.allowed_ext || []).map((e) => e.toLowerCase());
  const BLOCKED   = (enums.blocked_ext || []).map((e) => e.toLowerCase());
  const ACCEPT    = ALLOWED.map((e) => `.${e}`).join(',');

  const validateFile = (f) => {
    if (!f) return '';
    const name = (f.name || '').toLowerCase();
    const ext  = name.includes('.') ? name.split('.').pop() : '';
    if (!ext) return 'File must include an extension.';
    if (BLOCKED.includes(ext)) return `Executable files (.${ext}) are not allowed for security reasons.`;
    if (ALLOWED.length && !ALLOWED.includes(ext)) return `.${ext} is not an allowed file type. Allowed: ${ALLOWED.join(', ')}.`;
    if (f.size > MAX_BYTES) return `File is too large (${(f.size / 1024 / 1024).toFixed(1)} MB). Max ${(MAX_BYTES / 1024 / 1024).toFixed(0)} MB.`;
    if (f.size === 0) return 'File is empty.';
    return '';
  };

  const onFilePicked = (e) => {
    const f = e.target.files?.[0] || null;
    const err = validateFile(f);
    setFileError(err);
    setFile(err ? null : f);
    if (err && fileRef.current) fileRef.current.value = '';
  };

  const uploadFile = async (rid) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    await api.post(`/content-manager/resources/${rid}/versions`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (evt) => {
        if (evt.total) setUploadPct(Math.round((evt.loaded * 100) / evt.total));
      },
    });
  };

  const save = async () => {
    if (!set.title) return toast.error('Title is required');
    if (fileError) return toast.error(fileError);
    setSaving(true);
    setUploadPct(0);
    try {
      let resourceId = set.id;
      const payload = {
        title: set.title,
        description: set.description || '',
        category: set.category || null,
        tags: (typeof set.tags === 'string' ? set.tags.split(',').map((s) => s.trim()).filter(Boolean) : set.tags) || [],
        effective_date: set.effective_date || null,
        expiration_date: set.expiration_date || null,
        thumbnail_url: set.thumbnail_url || null,
        is_required: !!set.is_required,
        is_featured: !!set.is_featured,
        visibility_kind: set.visibility_kind,
        visibility_company_ids: set.visibility_company_ids || [],
        visibility_roles: set.visibility_roles || [],
        announcement_body: set.announcement_body || null,
        announcement_dismissable: !!set.announcement_dismissable,
        folder_path: set.folder_path || null,
        publish_at:  set.publish_at  || null,
        archive_at:  set.archive_at  || null,
      };
      if (isNew) {
        const r = await api.post('/content-manager/resources', { type: set.type, ...payload });
        resourceId = r.data?.id;
        if (file) {
          await uploadFile(resourceId);
          toast.success('Published successfully', {
            description: `${set.title} · ${file.name}`,
            action: { label: 'View', onClick: () => onSaved({ id: resourceId, viewNow: true }) },
          });
        } else {
          toast.success('Created (metadata only — no file attached)');
        }
        onSaved({ id: resourceId, isNew: true });
      } else {
        await api.patch(`/content-manager/resources/${resourceId}`, payload);
        if (file) {
          await uploadFile(resourceId);
          toast.success('New version published', {
            description: `${set.title} · ${file.name}`,
            action: { label: 'View', onClick: () => onSaved({ id: resourceId, viewNow: true }) },
          });
        } else {
          toast.success('Saved');
        }
        onSaved({ id: resourceId });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
      setUploadPct(0);
    }
  };

  return (
    <div className="fixed inset-0 z-[1100] bg-black/40 flex items-center justify-center" onClick={onClose} data-testid="pcm-editor-backdrop">
      <div className="bg-white rounded-lg shadow-xl w-[820px] max-w-[95vw] max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="pcm-editor">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h2 className="text-base font-semibold">{isNew ? 'New Resource' : `Edit — ${set.title || '(untitled)'}`}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>

        {/* ── File Upload ─────────────────────────────────────────── */}
        <div className="px-5 pt-4">
          <div className={`rounded-lg border-2 border-dashed p-4 ${fileError ? 'border-rose-300 bg-rose-50' : file ? 'border-emerald-300 bg-emerald-50/40' : 'border-slate-300 bg-slate-50'}`} data-testid="pcm-ed-drop">
            <div className="flex items-center gap-3">
              <Upload className={`h-5 w-5 ${file ? 'text-emerald-700' : 'text-slate-400'}`} />
              <div className="flex-1 min-w-0">
                <Label className="text-xs font-semibold">{isNew ? 'File (optional — you can add or replace it later)' : 'Replace / upload new version (optional)'}</Label>
                <input
                  ref={fileRef}
                  type="file"
                  accept={ACCEPT}
                  onChange={onFilePicked}
                  className="block text-xs mt-1 w-full"
                  data-testid="pcm-ed-file"
                />
                {file && !fileError && (
                  <p className="text-xs text-emerald-700 mt-1" data-testid="pcm-ed-file-ok">
                    <strong>{file.name}</strong> · {(file.size / 1024 / 1024).toFixed(2)} MB · {file.type || 'unknown MIME'}
                  </p>
                )}
                {fileError && (
                  <p className="text-xs text-rose-600 mt-1 flex items-center gap-1" data-testid="pcm-ed-file-err">
                    <AlertTriangle className="h-3 w-3" /> {fileError}
                  </p>
                )}
                {!file && !fileError && (
                  <p className="text-[10px] text-slate-500 mt-1">
                    Accepted: {ALLOWED.join(', ')} · Max {(MAX_BYTES / 1024 / 1024).toFixed(0)} MB · Executables blocked.
                  </p>
                )}
                {saving && file && uploadPct > 0 && (
                  <div className="mt-2 w-full h-1.5 bg-slate-200 rounded overflow-hidden" data-testid="pcm-ed-upload-bar">
                    <div className="h-full bg-emerald-600 transition-all" style={{ width: `${uploadPct}%` }} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Type</Label>
            <select value={set.type} onChange={(e) => setField('type', e.target.value)} className="w-full h-9 border rounded-md text-sm px-2 mt-1" disabled={!isNew} data-testid="pcm-ed-type">
              {enums.resource_types.map((t) => <option key={t} value={t}>{humanize(t)}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Category</Label>
            <Input value={set.category || ''} onChange={(e) => setField('category', e.target.value)} data-testid="pcm-ed-category" />
          </div>
          <div className="md:col-span-2"><Label className="text-xs">Title *</Label><Input value={set.title || ''} onChange={(e) => setField('title', e.target.value)} data-testid="pcm-ed-title" /></div>
          <div className="md:col-span-2"><Label className="text-xs">Description</Label><Textarea rows={2} value={set.description || ''} onChange={(e) => setField('description', e.target.value)} data-testid="pcm-ed-desc" /></div>
          <div><Label className="text-xs">Tags (comma separated)</Label><Input value={Array.isArray(set.tags) ? set.tags.join(', ') : (set.tags || '')} onChange={(e) => setField('tags', e.target.value)} data-testid="pcm-ed-tags" /></div>
          <div><Label className="text-xs">Folder path (e.g. Finance/2026/Q1)</Label><Input value={set.folder_path || ''} onChange={(e) => setField('folder_path', e.target.value)} data-testid="pcm-ed-folder" /></div>
          <div><Label className="text-xs">Thumbnail URL</Label><Input value={set.thumbnail_url || ''} onChange={(e) => setField('thumbnail_url', e.target.value)} data-testid="pcm-ed-thumb" /></div>
          <div><Label className="text-xs">Effective Date</Label><Input type="date" value={(set.effective_date || '').slice(0, 10)} onChange={(e) => setField('effective_date', e.target.value)} data-testid="pcm-ed-eff" /></div>
          <div><Label className="text-xs">Expiration Date</Label><Input type="date" value={(set.expiration_date || '').slice(0, 10)} onChange={(e) => setField('expiration_date', e.target.value)} data-testid="pcm-ed-exp" /></div>
          <div><Label className="text-xs">Publish at (schedule)</Label><Input type="datetime-local" value={set.publish_at ? new Date(set.publish_at).toISOString().slice(0,16) : ''} onChange={(e) => setField('publish_at', e.target.value ? new Date(e.target.value).toISOString() : '')} data-testid="pcm-ed-pubat" /></div>
          <div><Label className="text-xs">Archive at (schedule)</Label><Input type="datetime-local" value={set.archive_at ? new Date(set.archive_at).toISOString().slice(0,16) : ''} onChange={(e) => setField('archive_at', e.target.value ? new Date(e.target.value).toISOString() : '')} data-testid="pcm-ed-archat" /></div>
          <div>
            <Label className="text-xs">Visibility</Label>
            <select value={set.visibility_kind} onChange={(e) => setField('visibility_kind', e.target.value)} className="w-full h-9 border rounded-md text-sm px-2 mt-1" data-testid="pcm-ed-vis">
              {enums.visibility_kinds.map((k) => <option key={k} value={k}>{VISIBILITY_LABEL[k] || k}</option>)}
            </select>
          </div>
          <div className="flex items-end gap-4">
            <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={!!set.is_featured} onChange={(e) => setField('is_featured', e.target.checked)} data-testid="pcm-ed-featured" /> Featured</label>
            <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={!!set.is_required} onChange={(e) => setField('is_required', e.target.checked)} data-testid="pcm-ed-required" /> Required</label>
          </div>
          {set.visibility_kind === 'selected_companies' && (
            <div className="md:col-span-2">
              <Label className="text-xs">Company IDs (comma separated)</Label>
              <Input value={(set.visibility_company_ids || []).join(', ')} onChange={(e) => setField('visibility_company_ids', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} data-testid="pcm-ed-vis-companies" />
            </div>
          )}
          {set.visibility_kind === 'selected_roles' && (
            <div className="md:col-span-2">
              <Label className="text-xs">Roles (comma separated)</Label>
              <Input value={(set.visibility_roles || []).join(', ')} onChange={(e) => setField('visibility_roles', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} data-testid="pcm-ed-vis-roles" />
            </div>
          )}
          {set.type === 'announcements' && (
            <>
              <div className="md:col-span-2">
                <Label className="text-xs">Announcement Body</Label>
                <Textarea rows={3} value={set.announcement_body || ''} onChange={(e) => setField('announcement_body', e.target.value)} data-testid="pcm-ed-annbody" />
              </div>
              <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={!!set.announcement_dismissable} onChange={(e) => setField('announcement_dismissable', e.target.checked)} data-testid="pcm-ed-annd" /> Partner may dismiss</label>
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2 bg-slate-50">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="pcm-ed-cancel">Cancel</Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800" onClick={save} disabled={saving || !!fileError} data-testid="pcm-ed-save">
            {saving
              ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> {file && uploadPct > 0 ? `Uploading ${uploadPct}%` : 'Saving…'}</>
              : <><Save className="h-4 w-4 mr-1" /> {isNew ? (file ? 'Create & Upload' : 'Create') : (file ? 'Save & Upload' : 'Save changes')}</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

function VersionsModal({ resource, onClose }) {
  const [detail, setDetail] = useState(resource);
  const [downloading, setDownloading] = useState(null);
  useEffect(() => { (async () => { try { const r = await api.get(`/content-manager/resources/${resource.id}`); setDetail(r.data); } catch { /* noop */ } })(); }, [resource.id]);
  const versions = (detail.versions || []).slice().sort((a, b) => (b.uploaded_at || '').localeCompare(a.uploaded_at || ''));
  const downloadV = async (v) => {
    setDownloading(v.id);
    try {
      const r = await api.get(`/content-library/resources/${detail.id}/download`, { params: { version_id: v.id }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = v.original_filename || 'file'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); }
    finally { setDownloading(null); }
  };
  return (
    <div className="fixed inset-0 z-[1100] bg-black/40 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-[700px] max-w-[95vw] max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="pcm-versions-modal">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h2 className="text-base font-semibold">Version History — {detail.title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-2">
          {versions.length === 0 && <p className="text-sm text-slate-500 italic">No files uploaded yet.</p>}
          {versions.map((v) => (
            <div key={v.id} className={`border rounded p-3 flex items-center justify-between gap-3 ${v.id === detail.current_version_id ? 'border-emerald-400 bg-emerald-50/40' : ''}`} data-testid={`pcm-vrow-${v.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-semibold flex items-center gap-2">
                  {v.version}
                  {v.id === detail.current_version_id && <Badge className="bg-emerald-600 text-white text-[10px] py-0 h-4">Current</Badge>}
                </div>
                <div className="text-xs text-slate-500 truncate">{v.original_filename} · {(v.size / 1024).toFixed(1)} KB · {new Date(v.uploaded_at).toLocaleString()}</div>
                {v.notes && <div className="text-xs text-slate-600 mt-1">Notes: {v.notes}</div>}
              </div>
              <Button size="sm" variant="outline" onClick={() => downloadV(v)} disabled={downloading === v.id} data-testid={`pcm-vdl-${v.id}`}>
                {downloading === v.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><DownloadIcon className="h-3.5 w-3.5 mr-1" />Download</>}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DownloadsModal({ resource, onClose }) {
  const [rows, setRows] = useState([]);
  const [tab, setTab]   = useState('downloads');
  useEffect(() => { (async () => {
    try {
      const [dl, au] = await Promise.all([
        api.get(`/content-manager/resources/${resource.id}/downloads`),
        api.get(`/content-manager/resources/${resource.id}/audit`),
      ]);
      setRows({ downloads: dl.data?.items || [], audit: au.data?.items || [] });
    } catch { setRows({ downloads: [], audit: [] }); }
  })(); }, [resource.id]);
  const showRows = (rows[tab] || []);
  return (
    <div className="fixed inset-0 z-[1100] bg-black/40 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-[820px] max-w-[95vw] max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="pcm-log-modal">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h2 className="text-base font-semibold">Activity — {resource.title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-5 pt-3">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 max-w-md">
              <TabsTrigger value="downloads" data-testid="pcm-log-tab-downloads">Downloads / Views / Acks</TabsTrigger>
              <TabsTrigger value="audit"     data-testid="pcm-log-tab-audit">Admin Audit</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <div className="p-5">
          <table className="w-full text-xs" data-testid="pcm-log-table">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">When</th>
                <th className="text-left px-3 py-2">User</th>
                <th className="text-left px-3 py-2">Action</th>
                <th className="text-left px-3 py-2">Details</th>
              </tr>
            </thead>
            <tbody>
              {showRows.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400 italic">No entries.</td></tr>}
              {showRows.map((r, i) => (
                <tr key={r.id || i} className="border-t">
                  <td className="px-3 py-1.5 whitespace-nowrap text-slate-600">{new Date(r.at).toLocaleString()}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap">{r.user_email || r.actor_name || '—'}</td>
                  <td className="px-3 py-1.5"><Badge variant="outline" className="text-[10px] py-0 h-4">{r.action}</Badge></td>
                  <td className="px-3 py-1.5 text-slate-500">{r.company_id || r.actor_role || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
